import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../../lib/api';
import AdminShell from '../../components/admin/AdminShell';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import {
  Loader2, Copy, Ticket, Plus, RefreshCw, QrCode, Banknote, CreditCard,
  HelpCircle, X, CheckCircle2, XCircle, Clock,
} from 'lucide-react';

const PAYMENT_METHOD_META = {
  static_qr: { label: 'Static QR', icon: QrCode,     color: 'text-blue-300' },
  cash:      { label: 'Cash',      icon: Banknote,   color: 'text-emerald-300' },
  pos:       { label: 'POS Machine', icon: CreditCard, color: 'text-amber-300' },
  other:     { label: 'Other',     icon: HelpCircle, color: 'text-zinc-300' },
};

const STATUS_META = {
  issued:    { label: 'Active',    Icon: Clock,        cls: 'bg-amber-900/40 text-amber-300 border-amber-700' },
  redeemed:  { label: 'Redeemed',  Icon: CheckCircle2, cls: 'bg-emerald-900/40 text-emerald-300 border-emerald-700' },
  cancelled: { label: 'Cancelled', Icon: XCircle,      cls: 'bg-zinc-800 text-zinc-400 border-zinc-700' },
  expired:   { label: 'Expired',   Icon: Clock,        cls: 'bg-zinc-800 text-zinc-400 border-zinc-700' },
};

const fmtTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso; }
};

export default function GateCodesPage() {
  const [loading, setLoading] = useState(true);
  const [codes,   setCodes]   = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminApi.listGateCodes({ limit: 50 });
      setCodes(res.data.gate_codes || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load gate codes');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const cancelCode = async (codeId) => {
    try {
      await adminApi.cancelGateCode(codeId);
      toast.success('Code cancelled');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cancel failed');
    }
  };

  return (
    <AdminShell>
      <div className="max-w-5xl mx-auto py-6 px-4 space-y-6" data-testid="gate-codes-page">
        <header className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-['EB_Garamond'] font-semibold text-[#F2F2F2]">Gate Code Generator</h1>
            <p className="text-[#B9B9B9] text-sm mt-1">
              Generate a one-time 6-digit code after collecting payment at the gate (cash / static QR).
              The customer enters the code on <span className="text-mono text-[#D4A843]">/checkin/pay</span> to confirm their booking. Codes expire in 1 hour.
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={load} variant="outline" size="sm"
              className="bg-transparent border-[#3A2F16] text-[#D4A843] hover:bg-[#1F1A10]"
              data-testid="btn-refresh">
              <RefreshCw size={13} className="mr-1" /> Refresh
            </Button>
            <GenerateCodeDialog onCreated={load} />
          </div>
        </header>

        <Card className="bg-[#0F0F0F] border-[#262626]" data-testid="codes-list-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-[#F2F2F2] flex items-center gap-2">
              <Ticket size={16} className="text-[#D4A843]" /> Recent codes
            </CardTitle>
            <CardDescription className="text-[#B9B9B9] text-xs">
              Last 50. Click cancel to invalidate an unused code.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-[#D4A843]" /></div>
            ) : codes.length === 0 ? (
              <p className="text-[#6F6F6F] text-sm py-10 text-center" data-testid="empty-state">
                No codes yet. Click &quot;Generate Code&quot; to issue your first one.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[#6F6F6F] text-xs uppercase tracking-wider border-b border-[#262626]">
                      <th className="text-left py-2 px-2">Code</th>
                      <th className="text-left py-2 px-2">Amount</th>
                      <th className="text-left py-2 px-2">Method</th>
                      <th className="text-left py-2 px-2">Status</th>
                      <th className="text-left py-2 px-2">Issued</th>
                      <th className="text-left py-2 px-2">Issued By</th>
                      <th className="text-right py-2 px-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {codes.map((c) => {
                      const sm = STATUS_META[c.status] || STATUS_META.expired;
                      const pm = PAYMENT_METHOD_META[c.payment_method] || PAYMENT_METHOD_META.other;
                      return (
                        <tr key={c._id} className="border-b border-[#1F1F1F] last:border-0" data-testid={`code-row-${c.code}`}>
                          <td className="py-2 px-2 text-mono text-[#D4A843] font-semibold text-base tracking-wider">{c.code}</td>
                          <td className="py-2 px-2 text-[#F2F2F2]">₹{Number(c.amount).toLocaleString('en-IN')}</td>
                          <td className="py-2 px-2 text-[#F2F2F2]">
                            <span className="flex items-center gap-1.5">
                              <pm.icon size={12} className={pm.color} /> {pm.label}
                            </span>
                          </td>
                          <td className="py-2 px-2">
                            <Badge className={sm.cls + ' text-[10px]'}>
                              <sm.Icon size={9} className="mr-1" /> {sm.label}
                            </Badge>
                          </td>
                          <td className="py-2 px-2 text-[#B9B9B9] text-xs">{fmtTime(c.issued_at)}</td>
                          <td className="py-2 px-2 text-[#6F6F6F] text-xs truncate max-w-[150px]" title={c.issued_by}>{c.issued_by}</td>
                          <td className="py-2 px-2 text-right">
                            {c.status === 'issued' && (
                              <Button size="sm" variant="outline"
                                onClick={() => cancelCode(c._id)}
                                className="h-7 px-2 text-xs bg-transparent border-[#3A2F16] text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200"
                                data-testid={`code-cancel-${c.code}`}>
                                <X size={11} className="mr-0.5" /> Cancel
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminShell>
  );
}

function GenerateCodeDialog({ onCreated }) {
  const [open, setOpen]               = useState(false);
  const [amount, setAmount]           = useState('');
  const [paymentMethod, setPM]        = useState('static_qr');
  const [note, setNote]               = useState('');
  const [busy, setBusy]               = useState(false);
  const [created, setCreated]         = useState(null);

  useEffect(() => {
    if (!open) {
      setAmount(''); setPM('static_qr'); setNote(''); setCreated(null); setBusy(false);
    }
  }, [open]);

  const generate = async (e) => {
    e?.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount in ₹'); return; }
    setBusy(true);
    try {
      const res = await adminApi.createGateCode({
        amount: amt,
        payment_method: paymentMethod,
        note: note || null,
      });
      setCreated(res.data.gate_code);
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate code');
    } finally { setBusy(false); }
  };

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success('Copied'); };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="btn-gold" data-testid="btn-generate-code">
          <Plus size={14} className="mr-1" /> Generate Code
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0F0F0F] border-[#262626] text-[#F2F2F2] max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[#F2F2F2] font-['EB_Garamond']">
            {created ? 'Code Ready' : 'Generate Gate Code'}
          </DialogTitle>
          <DialogDescription className="text-[#B9B9B9] text-xs">
            {created
              ? 'Share this code with the customer. They enter it on the check-in payment page.'
              : 'Capture the payment details below. The customer enters the code on /checkin/pay.'}
          </DialogDescription>
        </DialogHeader>

        {created ? (
          <div className="space-y-4 py-2" data-testid="generated-code-view">
            <div className="bg-[#1A1A1A] rounded-xl p-6 text-center border border-[#3A2F16]">
              <p className="text-[#6F6F6F] text-xs uppercase tracking-wider mb-2">6-digit code</p>
              <p className="font-mono text-5xl text-[#D4A843] font-bold tracking-[0.3em] mb-3" data-testid="big-code">{created.code}</p>
              <Button onClick={() => copy(created.code)} variant="outline" size="sm"
                className="bg-transparent border-[#3A2F16] text-[#D4A843]" data-testid="btn-copy-code">
                <Copy size={11} className="mr-1" /> Copy
              </Button>
            </div>
            <div className="text-xs text-[#B9B9B9] space-y-1">
              <p>Amount: <span className="text-[#F2F2F2] font-semibold">₹{Number(created.amount).toLocaleString('en-IN')}</span></p>
              <p>Method: <span className="text-[#F2F2F2]">{PAYMENT_METHOD_META[created.payment_method]?.label || created.payment_method}</span></p>
              <p>Expires: <span className="text-[#F2F2F2]">{fmtTime(created.expires_at)}</span> (1 hour)</p>
              {created.note ? <p>Note: <span className="text-[#F2F2F2]">{created.note}</span></p> : null}
            </div>
            <DialogFooter>
              <Button onClick={() => setOpen(false)} className="btn-gold w-full" data-testid="btn-done">Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={generate} className="space-y-4 py-2" data-testid="generate-form">
            <div className="space-y-1">
              <Label htmlFor="amt" className="text-[#D4A843] text-xs">Amount (₹)</Label>
              <Input id="amt" type="number" min={1} step="1" value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="e.g. 1500"
                className="bg-[#1A1A1A] border-[#3A2F16] text-[#F2F2F2]"
                autoFocus
                data-testid="input-amount"
                required />
              <p className="text-[#6F6F6F] text-[10px]">Must match the check-in amount on the customer&apos;s side.</p>
            </div>

            <div className="space-y-2">
              <Label className="text-[#D4A843] text-xs">Payment Method</Label>
              <RadioGroup value={paymentMethod} onValueChange={setPM} className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {Object.entries(PAYMENT_METHOD_META).map(([key, meta]) => (
                  <label key={key}
                    className={`cursor-pointer rounded-lg border p-2 text-center transition ${
                      paymentMethod === key
                        ? 'bg-[#332710] border-[#D4A843]'
                        : 'bg-[#1A1A1A] border-[#3A2F16] hover:bg-[#222]'
                    }`}
                    data-testid={`pm-option-${key}`}>
                    <RadioGroupItem value={key} className="sr-only" />
                    <meta.icon size={16} className={`${meta.color} mx-auto mb-1`} />
                    <p className="text-[#F2F2F2] text-xs">{meta.label}</p>
                  </label>
                ))}
              </RadioGroup>
            </div>

            <div className="space-y-1">
              <Label htmlFor="note" className="text-[#D4A843] text-xs">Note (optional)</Label>
              <Input id="note" value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Receipt #482, table 7"
                className="bg-[#1A1A1A] border-[#3A2F16] text-[#F2F2F2]"
                data-testid="input-note" />
            </div>

            <DialogFooter className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={busy}
                className="bg-transparent border-[#3A2F16] text-[#B9B9B9]" data-testid="btn-cancel">
                Cancel
              </Button>
              <Button type="submit" disabled={busy || !amount} className="btn-gold" data-testid="btn-submit">
                {busy ? <Loader2 size={13} className="animate-spin mr-1" /> : null}
                Generate code
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
